import React from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormFields } from '../../../../AccountForm.meta';
import { useAccountFormContext } from '../../../../context/useAccountFormContext';
import { Box } from '../../../../../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../../../../../CommonComponents/OPL/Text/Text';
import { Tooltip } from '../../../../../../../../../../CommonComponents/OPL/Tooltip/Tooltip';
import { Icon } from '../../../../../../../../../../CommonComponents/OPL/Icon/Icon';
import { Select } from '../../../../../../../../../../CommonComponents/OPL/Select/Select/Select';
import { InputNumberMask } from '../../../../../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { selectCurrentClient } from '../../../../../../../../../../CommonSelectors/CommonAreaSelector';
import { OwnerTypeDTO } from '../../../../../../../../api/useAccountsQuery';

interface Props {
  fieldNamespace: 'DAFGrantSchedule' | 'DAFContributionSchedule';
}

export const DAFSchedule = ({ fieldNamespace }: Props) => {
  const formBag = useFormContext<FormFields>();

  const isGrant = fieldNamespace === 'DAFGrantSchedule';

  const {
    options: {
      IndividualOwnerOptions,
      DafOptions: { donationStartYearOptions, donationEndYearOptions },
    },
  } = useAccountFormContext();

  const startYearType = useWatch({
    name: `bankAccount.${fieldNamespace}.StartYear.Type`,
    control: formBag.control,
  });

  const endYearType = useWatch({
    name: `bankAccount.${fieldNamespace}.EndYear.Type`,
    control: formBag.control,
  });

  const ownerModel = useWatch({
    name: 'bankAccount.OwnerModel',
    control: formBag.control,
  });

  const { Dob: primaryDob, PartnerDob: spouseDob } = useSelector(selectCurrentClient);

  const primaryYob = primaryDob ? new Date(primaryDob).getFullYear() : null;
  const spouseYob = spouseDob ? new Date(spouseDob).getFullYear() : null;

  const selectedOwnerType =
    ownerModel.length === 1 &&
    IndividualOwnerOptions.People.find(val => val.Name === ownerModel[0].Name)?.OwnerType;

  const isPrimary = selectedOwnerType === OwnerTypeDTO.Principal;
  const isSpouse = selectedOwnerType === OwnerTypeDTO.Spouse;

  const calculateAgeLabel = (year: number | null) => {
    if (year === null || !Number.isInteger(year)) {
      return undefined;
    }

    if (isPrimary && primaryYob) {
      return `Age ${year - primaryYob}`;
    }

    if (isSpouse && spouseYob) {
      return `Age ${year - spouseYob}`;
    }

    return undefined;
  };

  const donationStartYearOptionsWithAgeLabel = donationStartYearOptions.map(opt => ({
    ...opt,
    rightLabel: calculateAgeLabel(opt.value),
  }));

  const donationEndYearOptionsWithAgeLabel = donationEndYearOptions.map(opt => ({
    ...opt,
    rightLabel: calculateAgeLabel(opt.value),
  }));

  return (
    <>
      <Box display="flex" alignItems="center" css="gap: 8px">
        <Text as="p" variant="formLabel" color="neutral.600">
          {isGrant ? 'Grant' : 'Donation'}
        </Text>
        <Tooltip
          content={
            isGrant
              ? 'The year your donation is sent to the charity of your choice.'
              : 'The year you make your gift to the donor-advised fund.'
          }
        >
          <Icon className="far fa-question-circle" fontSize="h6" color="neutral.500" />
        </Tooltip>
      </Box>

      <Box
        css={`
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        `}
      >
        <Controller
          control={formBag.control}
          name={`bankAccount.${fieldNamespace}.StartYear.Year`}
          render={({ field: { value } }) => {
            return (
              <Select
                isPortal
                value={donationStartYearOptions.find(option => {
                  if (value === null) {
                    return option.type === startYearType;
                  }

                  return option.value === value;
                })}
                options={donationStartYearOptionsWithAgeLabel}
                onChange={selection => {
                  if (selection) {
                    formBag.setValue(
                      `bankAccount.${fieldNamespace}.StartYear.Year`,
                      selection.value,
                    );

                    formBag.setValue(
                      `bankAccount.${fieldNamespace}.StartYear.Type`,
                      selection.type,
                    );
                  }
                }}
                label={`${isGrant ? 'Grant' : 'Donation'} Start Year`}
              />
            );
          }}
        />

        <Controller
          control={formBag.control}
          name={`bankAccount.${fieldNamespace}.EndYear.Year`}
          render={({ field: { value } }) => {
            return (
              <Select
                isPortal
                value={donationEndYearOptions.find(option => {
                  if (value === null) {
                    return option.type === endYearType;
                  }

                  return option.value === value;
                })}
                options={donationEndYearOptionsWithAgeLabel}
                onChange={selection => {
                  if (selection) {
                    formBag.setValue(`bankAccount.${fieldNamespace}.EndYear.Year`, selection.value);
                    formBag.setValue(`bankAccount.${fieldNamespace}.EndYear.Type`, selection.type);
                  }
                }}
                label={`${isGrant ? 'Grant' : 'Donation'} End Year`}
              />
            );
          }}
        />
      </Box>

      <Box>
        <Controller
          control={formBag.control}
          name={`bankAccount.${fieldNamespace}.Amount`}
          render={({ field }) => (
            <InputNumberMask
              {...field}
              label={`${isGrant ? 'Grant' : 'Donation'} Amount`}
              innerText="/annually"
              leftIcon="far fa-dollar-sign"
              decimalScale={2}
              allowNegative={false}
            />
          )}
        />
      </Box>
    </>
  );
};
